import { useMemo, useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";

export default function AdminEmbedPanel() {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  // Section toggles
  const [mode, setMode] = useState("both"); // vote | results | both
  const [topCount, setTopCount] = useState(3);
  const [showHeader, setShowHeader] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [showTitle, setShowTitle] = useState(true);
  const [showDescription, setShowDescription] = useState(false);
  const [showCountdown, setShowCountdown] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [showFooter, setShowFooter] = useState(true);
  const [compact, setCompact] = useState(false);

  // Theme
  const [theme, setTheme] = useState("light"); // light|dark|transparent
  const [accent, setAccent] = useState("#2e6c2e");
  const [radius, setRadius] = useState(20);
  const [customBg, setCustomBg] = useState("");

  // Iframe
  const [width, setWidth] = useState(480);
  const [height, setHeight] = useState(560);
  const [copied, setCopied] = useState(false);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (mode !== "both") params.set("mode", mode);
    params.set("top", String(topCount));
    if (!showHeader) params.set("header", "0");
    if (!showLogo) params.set("logo", "0");
    if (!showTitle) params.set("title", "0");
    if (showDescription) params.set("description", "1");
    if (!showCountdown) params.set("countdown", "0");
    if (showShare) params.set("share", "1");
    if (!showFooter) params.set("footer", "0");
    if (compact) params.set("compact", "1");
    if (theme !== "light") params.set("theme", theme);
    if (accent.toLowerCase() !== "#2e6c2e") params.set("accent", accent.replace("#", ""));
    if (radius !== 20) params.set("radius", String(radius));
    if (customBg) params.set("bg", customBg.replace("#", ""));
    return `${baseUrl}/widget?${params.toString()}`;
  }, [
    baseUrl,
    mode,
    topCount,
    showHeader,
    showLogo,
    showTitle,
    showDescription,
    showCountdown,
    showShare,
    showFooter,
    compact,
    theme,
    accent,
    radius,
    customBg,
  ]);

  const snippet = useMemo(
    () =>
      `<iframe\n  src="${url}"\n  width="100%"\n  height="${height}"\n  frameborder="0"\n  style="border:0;max-width:${width}px;display:block;margin:0 auto;background:transparent"\n  allow="clipboard-write"\n  title="Scootive On Tour — głosuj"\n></iframe>`,
    [url, height, width]
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Skopiowano kod embed");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Nie udało się skopiować");
    }
  };

  const Section = ({ title, children }) => (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-black/55 mb-2">{title}</p>
      {children}
    </div>
  );

  const SegBtn = ({ active, onClick, children, tid }) => (
    <button
      type="button"
      data-testid={tid}
      onClick={onClick}
      className={`px-3 h-9 rounded-full text-xs font-medium transition-all ${
        active ? "bg-brand-green text-white shadow-sm" : "bg-black/5 text-black/65 hover:bg-black/10"
      }`}
    >
      {children}
    </button>
  );

  const ToggleRow = ({ label, checked, onChange, tid }) => (
    <label className="flex items-center justify-between p-3 rounded-xl bg-black/[0.03]">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={tid} />
    </label>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      {/* CONFIG */}
      <div className="surface-soft p-5 sm:p-6 space-y-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight mb-1">
            Konfiguracja embed
          </h3>
          <p className="text-xs text-black/55">
            Dostosuj jak ma wyglądać widget osadzony na zewnętrznej stronie.
          </p>
        </div>

        <Section title="Tryb widgetu">
          <div className="flex items-center gap-2 flex-wrap">
            <SegBtn active={mode === "both"} onClick={() => setMode("both")} tid="embed-mode-both">
              Głosowanie + wyniki
            </SegBtn>
            <SegBtn active={mode === "vote"} onClick={() => setMode("vote")} tid="embed-mode-vote">
              Tylko głosowanie
            </SegBtn>
            <SegBtn
              active={mode === "results"}
              onClick={() => setMode("results")}
              tid="embed-mode-results"
            >
              Tylko wyniki
            </SegBtn>
          </div>
        </Section>

        <Section title="Liczba pozycji w rankingu">
          <div className="flex items-center gap-2 flex-wrap">
            {[0, 3, 5, 10].map((v) => (
              <SegBtn
                key={v}
                active={topCount === v}
                onClick={() => setTopCount(v)}
                tid={`embed-top-${v}`}
              >
                {v === 0 ? "Brak rankingu" : `TOP ${v}`}
              </SegBtn>
            ))}
          </div>
        </Section>

        <Section title="Motyw">
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { id: "light", label: "Jasny" },
              { id: "dark", label: "Ciemny" },
              { id: "transparent", label: "Przezroczysty" },
            ].map((t) => (
              <SegBtn key={t.id} active={theme === t.id} onClick={() => setTheme(t.id)} tid={`embed-theme-${t.id}`}>
                {t.label}
              </SegBtn>
            ))}
          </div>
        </Section>

        <div className="grid grid-cols-2 gap-3">
          <Section title="Kolor akcent">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                data-testid="embed-accent-color"
                className="h-10 w-12 rounded-lg border border-black/10 cursor-pointer"
              />
              <Input
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                className="bg-white border-black/10 input-rounded h-10 text-xs font-mono-disp"
              />
            </div>
          </Section>

          <Section title="Tło karty (opcjonalne)">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={customBg || "#ffffff"}
                onChange={(e) => setCustomBg(e.target.value)}
                data-testid="embed-bg-color"
                className="h-10 w-12 rounded-lg border border-black/10 cursor-pointer"
              />
              <Input
                value={customBg}
                onChange={(e) => setCustomBg(e.target.value)}
                placeholder="np. #ffffff"
                className="bg-white border-black/10 input-rounded h-10 text-xs font-mono-disp"
              />
            </div>
          </Section>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Section title="Zaokrąglenie">
            <Input
              type="number"
              min={0}
              max={40}
              value={radius}
              onChange={(e) => setRadius(Math.max(0, Math.min(40, parseInt(e.target.value || "0", 10))))}
              className="bg-white border-black/10 input-rounded h-10"
              data-testid="embed-radius-input"
            />
          </Section>
          <Section title="Szerokość iframe">
            <Input
              type="number"
              min={280}
              max={1200}
              value={width}
              onChange={(e) => setWidth(Math.max(280, Math.min(1200, parseInt(e.target.value || "0", 10))))}
              className="bg-white border-black/10 input-rounded h-10"
              data-testid="embed-width-input"
            />
          </Section>
          <Section title="Wysokość iframe">
            <Input
              type="number"
              min={200}
              max={1400}
              value={height}
              onChange={(e) => setHeight(Math.max(200, Math.min(1400, parseInt(e.target.value || "0", 10))))}
              className="bg-white border-black/10 input-rounded h-10"
              data-testid="embed-height-input"
            />
          </Section>
        </div>

        <Section title="Elementy do pokazania">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <ToggleRow label="Pasek nagłówka" checked={showHeader} onChange={setShowHeader} tid="embed-header-switch" />
            <ToggleRow label="Logo Scootive" checked={showLogo} onChange={setShowLogo} tid="embed-logo-switch" />
            <ToggleRow label="Tytuł kampanii" checked={showTitle} onChange={setShowTitle} tid="embed-title-switch" />
            <ToggleRow label="Opis kampanii" checked={showDescription} onChange={setShowDescription} tid="embed-desc-switch" />
            <ToggleRow label="Countdown" checked={showCountdown} onChange={setShowCountdown} tid="embed-countdown-switch" />
            <ToggleRow label="Przyciski share" checked={showShare} onChange={setShowShare} tid="embed-share-switch" />
            <ToggleRow label="Stopka + link" checked={showFooter} onChange={setShowFooter} tid="embed-footer-switch" />
            <ToggleRow label="Tryb kompaktowy" checked={compact} onChange={setCompact} tid="embed-compact-switch" />
          </div>
        </Section>

        <div className="pt-3 border-t border-black/5">
          <p className="text-[11px] font-medium uppercase tracking-wider text-black/55 mb-2">
            Adres widgetu
          </p>
          <div className="flex items-center gap-2">
            <Input
              data-testid="embed-url-input"
              value={url}
              readOnly
              className="bg-white border-black/10 input-rounded h-10 text-xs font-mono-disp"
            />
            <Button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(url);
                toast.success("Skopiowano URL");
              }}
              className="btn-soft h-10 px-3"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* SNIPPET + PREVIEW */}
      <div className="space-y-5">
        <div className="surface-soft p-5 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl uppercase tracking-tight">Kod do wklejenia</h3>
            <Button
              type="button"
              onClick={copy}
              data-testid="embed-copy-btn"
              className="btn-brand h-9 px-4 gap-2 text-xs font-semibold"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Skopiowano" : "Kopiuj kod"}
            </Button>
          </div>
          <pre
            data-testid="embed-snippet"
            className="bg-[#0e1410] text-[#A8D8A8] rounded-xl p-4 text-[11px] leading-relaxed font-mono-disp overflow-auto max-h-56 whitespace-pre-wrap break-all"
          >
            {snippet}
          </pre>
          <p className="text-[11px] text-black/55 mt-3 leading-relaxed">
            Wklej w HTML strony (np. WordPress &rarr; blok HTML). Widget responsywny — max{" "}
            {width}px.
          </p>
        </div>

        <div className="surface-soft p-5 sm:p-6">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-black/45 mb-3">
            Podgląd na żywo
          </p>
          <div className="bg-[#f3f4f3] rounded-xl p-3 flex justify-center">
            <iframe
              key={url + height + width}
              data-testid="embed-preview"
              src={url}
              width="100%"
              height={height}
              frameBorder="0"
              style={{ border: 0, maxWidth: width, background: "transparent" }}
              title="Embed preview"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
